import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import {
    channelsHandler,
    handleChannelRemove,
    handleGroupDevicesFilter,
    handleReallocateChannels,
    moveWithinList,
} from './helpers';

const upcomingInitialState = {
    filters: {},
    selectedNodes: [],
    secondary: [],
    maxCount: 6,
    viewAllButton: false,
    isTotalCountVisible: false,
    countMode: 'custom',
};

type GenericObject = Record<string, unknown>;
type UpcomingFilters = GenericObject & {
    deviceId?: unknown;
    deviceIds?: unknown;
};

const toObject = (value: unknown): GenericObject =>
    value && typeof value === 'object' ? ({ ...value } as GenericObject) : {};

const hasItems = (value: unknown): boolean => Array.isArray(value) && value.length > 0;

const shouldMigrateDeviceId = (filters: UpcomingFilters): boolean =>
    hasItems(filters.deviceId) && !hasItems(filters.deviceIds);

const normalizeUpcomingPayload = (payload: unknown): Record<string, unknown> => {
    const normalizedPayload = toObject(payload);
    const filters = toObject(normalizedPayload.filters) as UpcomingFilters;

    if (shouldMigrateDeviceId(filters)) {
        const nextFilters: UpcomingFilters = {
            ...filters,
            deviceIds: filters.deviceId,
        };
        delete nextFilters.deviceId;
        normalizedPayload.filters = nextFilters;
    }

    return normalizedPayload;
};

const upcomingWidgetSlice = createSlice({
    name: 'upcoming',
    initialState: upcomingInitialState,
    reducers: {
        resetUpcomingConfiguration: () => {
            return upcomingInitialState;
        },
        setPredefinedUpcoming: (state: RootState, action: PayloadAction<any>) => {
            const normalizedPayload = normalizeUpcomingPayload(action.payload);

            Object.keys(normalizedPayload)?.forEach((key: string) => {
                state[key] = normalizedPayload[key];
            });
        },
        setDevice: (state: RootState, action: PayloadAction<any>) => {
            channelsHandler(state, action);
        },
        removeUpcomingNode: (state: RootState, action: PayloadAction<any>) => {
            handleChannelRemove(state, action, 'id');
        },
        setUpcomingFilters: (state: RootState, action: PayloadAction<any>) => {
            state['filters'] = action.payload;
        },
        setUpcomingSelectedNodes: (state: RootState, action: PayloadAction<any>) => {
            handleGroupDevicesFilter(state, action);
        },
        setUpcomingData: (state: RootState, action: PayloadAction<any>) => {
            state.secondary = action.payload;
        },
        setUpcomingPreferences: (state: any, action: PayloadAction<any>) => {
            state[action.payload.key] = action.payload.value;
        },
        reallocateUpcomingFilters: (state: any, action: PayloadAction<any>) => {
            handleReallocateChannels(state, action);
        },
        moveBetweenUpcomings: (state: RootState, action: PayloadAction<any>) => {
            moveWithinList(state, action);
        },
    },
    extraReducers: (builder) => {
        builder.addCase('widgetConfiguration/toggleDeviceLinkMode', (state: RootState) => {
            state.secondary = [];
            state.selectedNodes = [];
            state.filters = {};
        });
        builder.addCase('widgetConfiguration/setWidgetDevice', (state: RootState, action: any) => {
            if (action.payload.reset) {
                state.secondary = [];
            }
            state['deviceId'] = action.payload?.data?.id;
        });
    },
});

export const {
    setDevice,
    removeUpcomingNode,
    setUpcomingData,
    moveBetweenUpcomings,
    setUpcomingPreferences,
    setUpcomingFilters,
    setUpcomingSelectedNodes,
    resetUpcomingConfiguration,
    setPredefinedUpcoming,
    reallocateUpcomingFilters,
} = upcomingWidgetSlice.actions;

export default upcomingWidgetSlice;
